import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Sparkles, X, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * AI-assisted welcome onboarding.
 * Auto-opens once for new users (when !onboarding_completed && !onboarding_skipped).
 * Can also be reopened manually from "Mi Perfil" via window event `teko-onboarding-open`.
 * Free for everyone (does NOT consume premium AI credits — uses the same flow as tutorial chat).
 */
const OnboardingChat = () => {
  const { user, refreshUser } = useAuth();
  const { theme } = useTheme();
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content}[]
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState(null); // when AI finishes
  const listRef = useRef(null);
  const startedRef = useRef(false);

  // Auto-open exactly once for new users
  useEffect(() => {
    if (!user) return;
    if (user.onboarding_completed || user.onboarding_skipped) return;
    if (startedRef.current) return;
    startedRef.current = true;
    setOpen(true);
  }, [user]);

  // Manual reopen from "Mi Perfil"
  useEffect(() => {
    const onOpen = () => {
      setMessages([]);
      setProfile(null);
      setOpen(true);
    };
    window.addEventListener('teko-onboarding-open', onOpen);
    return () => window.removeEventListener('teko-onboarding-open', onOpen);
  }, []);

  // Send the empty turn to get the AI's first question when the panel opens.
  useEffect(() => {
    if (open && messages.length === 0 && !busy && !profile) {
      void send(null, []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open, busy]);

  const send = async (text, history) => {
    const baseHistory = history ?? messages;
    const nextMessages = text ? [...baseHistory, { role: 'user', content: text }] : baseHistory;
    if (text) {
      setMessages(nextMessages);
      setInput('');
    }
    setBusy(true);
    try {
      const { data } = await api.post('/onboarding/chat', { messages: nextMessages, lang });
      if (data.profile) {
        // AI signaled completion — strip the JSON block from the visible message
        const visible = (data.response || '').split('PROFILE_JSON')[0].replace(/```$/g, '').replace(/```\s*$/g, '').trim();
        if (visible) setMessages([...nextMessages, { role: 'assistant', content: visible }]);
        setProfile({
          bio: data.profile.bio || '',
          interests: Array.isArray(data.profile.interests) ? data.profile.interests : [],
          goals_focus: Array.isArray(data.profile.goals_focus) ? data.profile.goals_focus : [],
        });
      } else {
        setMessages([...nextMessages, { role: 'assistant', content: data.response }]);
      }
    } catch (err) {
      setMessages([...nextMessages, { role: 'assistant', content: 'No pude responder ahora. Inténtalo de nuevo en unos segundos o pulsa "Saltar".' }]);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || busy) return;
    send(input.trim());
  };

  const skip = async () => {
    try {
      await api.post('/onboarding/skip', { skipped: true });
      await refreshUser?.();
    } catch { /* non-blocking */ }
    setOpen(false);
    toast.success('Listo. Puedes completar tu perfil cuando quieras desde "Mi Perfil".');
  };

  const finish = async () => {
    if (!profile) return;
    setBusy(true);
    try {
      await api.post('/onboarding/complete', profile);
      await refreshUser?.();
      toast.success('¡Perfil guardado!');
      setOpen(false);
    } catch (err) {
      toast.error('No pude guardar el perfil. Intenta de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  if (!open || !user) return null;

  return (
    <div
      className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center p-2 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
      data-testid="onboarding-modal"
    >
      <div
        className="relative w-full max-w-lg rounded-3xl overflow-hidden flex flex-col"
        style={{
          background: theme.card,
          border: `1px solid ${theme.accent}55`,
          boxShadow: `0 28px 80px ${theme.accent}40`,
          maxHeight: 'min(80vh, 720px)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${theme.border}` }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${theme.accent}25`, border: `1px solid ${theme.accent}60` }}>
              <Sparkles size={18} style={{ color: theme.accent }} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest" style={{ color: theme.textMuted }}>Bienvenida</p>
              <h3 className="text-base font-medium" style={{ color: theme.textPrimary }}>Conozcámonos un momento</h3>
            </div>
          </div>
          <button onClick={skip} aria-label="Saltar" data-testid="onboarding-skip-x"
            className="p-1 hover:opacity-70 transition" style={{ color: theme.textMuted }}>
            <X size={18} />
          </button>
        </div>

        {/* Messages or profile preview */}
        <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="onboarding-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className="max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-relaxed"
                style={{
                  background: m.role === 'user' ? theme.primary : theme.elevated,
                  color: m.role === 'user' ? '#fff' : theme.textPrimary,
                  border: m.role === 'user' ? 'none' : `1px solid ${theme.border}`,
                }}
              >
                {m.content}
              </div>
            </div>
          ))}
          {busy && !profile && (
            <div className="flex justify-start">
              <div className="rounded-2xl px-3.5 py-2 text-sm flex items-center gap-2"
                style={{ background: theme.elevated, color: theme.textMuted, border: `1px solid ${theme.border}` }}>
                <Loader2 size={12} className="animate-spin" /> Pensando...
              </div>
            </div>
          )}

          {/* Profile confirmation card */}
          {profile && (
            <div className="rounded-2xl p-4 mt-4 space-y-3"
              style={{ background: `${theme.success}15`, border: `1px solid ${theme.success}50` }}
              data-testid="onboarding-profile-preview">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} style={{ color: theme.success }} />
                <p className="text-sm font-medium" style={{ color: theme.textPrimary }}>Esto es lo que entendí de ti</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: theme.textMuted }}>Resumen</p>
                <textarea
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  className="w-full text-sm p-2 rounded-md outline-none"
                  rows={3}
                  style={{ background: theme.elevated, color: theme.textPrimary, border: `1px solid ${theme.border}` }}
                  data-testid="onboarding-bio-edit"
                />
              </div>
              {profile.interests.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: theme.textMuted }}>Intereses</p>
                  <div className="flex flex-wrap gap-1.5">
                    {profile.interests.map((it, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 rounded-full"
                        style={{ background: `${theme.accent}20`, color: theme.accent, border: `1px solid ${theme.accent}40` }}>
                        {it}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {profile.goals_focus.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: theme.textMuted }}>Foco para los próximos meses</p>
                  <ul className="text-sm space-y-1" style={{ color: theme.textSecondary }}>
                    {profile.goals_focus.map((g, i) => <li key={i}>· {g}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Composer or finish actions */}
        {profile ? (
          <div className="px-4 py-3 flex items-center justify-end gap-2" style={{ borderTop: `1px solid ${theme.border}` }}>
            <button onClick={skip} className="text-xs underline" style={{ color: theme.textMuted }}
              data-testid="onboarding-skip">
              Saltar
            </button>
            <Button onClick={finish} disabled={busy} size="sm"
              style={{ background: theme.accent, color: theme.bg }}
              data-testid="onboarding-finish">
              {busy ? <Loader2 size={14} className="animate-spin" /> : 'Guardar perfil'}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-4 py-3 flex items-center gap-2" style={{ borderTop: `1px solid ${theme.border}` }}>
            <button type="button" onClick={skip} className="text-xs underline shrink-0" style={{ color: theme.textMuted }}
              data-testid="onboarding-skip">
              Saltar
            </button>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Responde aquí..."
              disabled={busy}
              className="flex-1 bg-transparent outline-none text-sm px-2 py-2 rounded-md"
              style={{ color: theme.textPrimary, border: `1px solid ${theme.border}`, background: theme.elevated }}
              data-testid="onboarding-input"
            />
            <button type="submit" disabled={busy || !input.trim()}
              className="p-2 rounded-full disabled:opacity-40"
              style={{ background: theme.accent, color: theme.bg }}
              data-testid="onboarding-send" aria-label="Enviar">
              <Send size={14} />
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default OnboardingChat;
